import React, { useCallback } from 'react';
import styled from '@emotion/styled';
import { isGemAU, setSessionStorage } from 'utils';
import { storageStateKey, useStoreValue } from 'store';
import { STEP_OUTCOME, STEP_VERIFY_IDENTITY, PRODUCT_CODE_GO, config } from '_config';
import { defaultsDeep } from 'lodash';
import { getDefaultState } from 'store/initialState';
import { useHistory } from 'react-router-dom';
import { auAcquisitionComplete, nzV2AcquisitionComplete } from './mockState';
import {
  DECLINED,
  REFERRED,
  PENDING,
  APPROVED,
  SERVICE_UNAVAILABLE,
  POI_CHECK_REQUIRED,
  POI_CHECK_PENDING,
  CONDITIONALLY_APPROVED,
  PROOF_OF_ADDRESS,
  ANTI_MONEY_LAUNDERING,
  PROOF_OF_INCOME,
  PROOF_OF_ADDRESS_AND_PROOF_OF_INCOME,
  ANTI_MONEY_LAUNDERING_AND_PROOF_OF_INCOME,
  BIOMETRICS_CHECK_REQUIRED,
  BIOMETRICS_CONSENT_PENDING,
  IDV_CHECK_REQUIRED,
  IDV_CHECK_PENDING,
  CCR_REQUIRED,
  QUOTE_INITIAL,
  QUOTE_DECLINE,
  POITYPE_DOC_UPLOAD,
} from '../store/constants';

import {
  STEP_LOAN_INFORMATION,
  STEP_CONFIRM_CREDIT_LIMIT,
  STEP_DVS_CONSENT,
  STEP_PROCESSING_IDV,
  STEP_EIDV,
  STEP_AUS_POST,
  STEP_VERIFY_INSTORE,
  STEP_ID_DOC_UPLOAD,
  STEP_FACIAL_BIOMETRICS_CONSENT,
  STEP_CREDIT_LIMIT_ALLOCATION,
} from '../_config';

export function DevTeleport({ setAllowNavigationToAllRoutes }) {
  const [storeState, updateState] = useStoreValue();
  const history = useHistory();
  const teleport = useCallback(
    async index => {
      setAllowNavigationToAllRoutes();
      const selectedOption = options()[config.countryCode][index];
      const state = buildState(selectedOption.getState());
      setSessionStorage(storageStateKey, state);
      updateState(state);
      history.push(selectedOption.path);
    },
    [history, setAllowNavigationToAllRoutes, updateState],
  );

  return (
    <Wrapper>
      {options()[config.countryCode].map(({ label, condition, key }, index) =>
        !condition || condition({ storeState, config }) ? (
          <button key={key} onClick={() => teleport(index)} type="button" data-testid={key}>
            {label}
          </button>
        ) : null,
      )}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
  border: 1px solid #d6ebff;

  button {
    padding: 0.5rem 1rem;
    background-color: #d6ebff;
    border: none;
    cursor: pointer;
    font-weight: 600;
  }
`;

const options = () => {
  const nzV2 = [
    {
      label: 'Saved for later',
      key: 'saved-for-later',
      path: '/saved',
      getState: () => ({
        acquisition: nzV2AcquisitionComplete,
        applicationSubmitted: true,
        applicationStatus: POI_CHECK_REQUIRED,
        applicationErrors: null,
        applicationStartDate: new Date().toISOString(),
        gemVisaAccountNumber: '5218 9435 0015 199',
      }),
    },
    {
      label: 'Resume - Expired',
      key: 'resume-expired',
      path: '/resume-application-expired',
      getState: () => ({
        acquisition: nzV2AcquisitionComplete,
        applicationStatus: IDV_CHECK_REQUIRED,
        isEmailVerified: true,
        isLatitudeIdPasswordSet: true,
        applicationSubmitted: true,
        applicationStartDate: new Date().toISOString(),
        applicationErrors: null,
        creditLimitApproved: 3000,
      }),
    },
    {
      label: 'Commitments',
      key: 'commitments',
      path: '/commitments',
      getState: () => ({
        acquisition: nzV2AcquisitionComplete,
        applicationSubmitted: false,
        applicationStartDate: new Date().toISOString(),
      }),
    },
    {
      label: 'Additional Card Holder',
      key: 'additional-card-holder',
      path: '/additional-card-holder',
      getState: () => ({
        acquisition: nzV2AcquisitionComplete,
        applicationSubmitted: false,
        applicationStartDate: new Date().toISOString(),
      }),
    },
    {
      label: 'Balance Transfer',
      key: 'balance-transfer',
      path: '/balance-transfer',
      getState: () => ({
        acquisition: nzV2AcquisitionComplete,
        applicationSubmitted: false,
        applicationStartDate: new Date().toISOString(),
      }),
    },
    {
      label: 'Processing Submission',
      key: 'processing-submission',
      path: '/processing',
      getState: () => ({
        acquisition: nzV2AcquisitionComplete,
        applicationSubmitted: true,
        applicationStatus: PENDING,
        applicationErrors: null,
        applicationStartDate: new Date().toISOString(),
      }),
    },
    // {
    //   label: 'CCR Loan Information',
    //   key: 'ccr-loan-information',
    //   path: STEP_LOAN_INFORMATION,
    //   getState: () => ({
    //     acquisition: {
    //       ...nzV2AcquisitionComplete,
    //       otherDetails: {
    //         ...nzV2AcquisitionComplete.otherDetails,
    //         residentialStatus: 'MORTGAGE',
    //       },
    //     },
    //     applicationStatus: CCR_REQUIRED,
    //     applicationSubmitted: true,
    //     applicationStartDate: new Date().toISOString(),
    //     applicationErrors: null,
    //     undisclosedDebts: [
    //       {
    //         id: 1,
    //         source: 'EQUIFAX_AUSTRALIA',
    //         category: 'HOME_LOAN',
    //         creditProvider: 'ANZ',
    //         currentLimit: 10000.0,
    //       },
    //       {
    //         id: 2,
    //         source: 'EQUIFAX_AUSTRALIA',
    //         category: 'HOME_LOAN',
    //         creditProvider: 'ANZ',
    //         currentLimit: 20000.0,
    //       },
    //       {
    //         id: 3,
    //         source: 'EQUIFAX_AUSTRALIA',
    //         category: 'CREDIT_CARD',
    //         creditProvider: 'Latitude',
    //         currentLimit: 3000.0,
    //       },
    //     ],
    //   }),
    // },
    {
      label: 'Dynamic Credit Limit - Conditionally Approved',
      key: 'dynamic-credit-limit-conditionally-approved',
      path: STEP_CONFIRM_CREDIT_LIMIT,
      getState: () => ({
        acquisition: {
          ...nzV2AcquisitionComplete,
          confirmCreditLimit: { newCreditLimit: '', creditLimitOptions: null },
        },
        applicationStatus: QUOTE_INITIAL,
        applicationSubmitted: true,
        applicationStartDate: new Date().toISOString(),
        applicationErrors: null,
        creditLimitApproved: 7000,
      }),
    },
    {
      label: 'Dynamic Credit Limit - Declined',
      key: 'dynamic-credit-limit-declined',
      path: STEP_CONFIRM_CREDIT_LIMIT,
      getState: () => ({
        acquisition: {
          ...{
            overview: {
              consentGiven: true,
              hasReadImportantDocuments: true,
            },
            contactDetails: {},
            password: {},
            personalDetails: {},
            otherDetails: {},
            extraDetails: {},
            employmentDetails: {},
            expensesDetails: {},
            additionalDebtsDetails: {},
            confirmCreditLimit: { referred: true },
            preferredCreditLimitDetails: { referred: true },
          },
          confirmReferredCreditLimit: {
            newCreditLimit: '',
          },
        },
        applicationStatus: QUOTE_DECLINE,
        applicationSubmitted: true,
        applicationStartDate: new Date().toISOString(),
        applicationErrors: null,
      }),
    },
    {
      label: 'Latitude ID - Introduction',
      key: 'latitude-id-introduction',
      path: '/latitude-id-introduction',
      getState: () => ({
        acquisition: nzV2AcquisitionComplete,
        applicationSubmitted: true,
        applicationStatus: IDV_CHECK_REQUIRED,
        applicationErrors: null,
        applicationStartDate: new Date().toISOString(),
        isEmailVerified: false,
        isLatitudeIdPasswordSet: false,
      }),
    },
    {
      label: 'Latitude ID - Password',
      key: 'latitude-id-password',
      path: '/latitude-id',
      getState: () => ({
        acquisition: nzV2AcquisitionComplete,
        applicationSubmitted: true,
        applicationStatus: IDV_CHECK_REQUIRED,
        applicationErrors: null,
        applicationStartDate: new Date().toISOString(),
        isEmailVerified: true,
        isLatitudeIdPasswordSet: false,
      }),
    },
    {
      label: 'IDV consent',
      key: 'idv-consent',
      path: STEP_DVS_CONSENT,
      getState: () => ({
        activeStep: STEP_DVS_CONSENT,
        applicationStatus: IDV_CHECK_REQUIRED,
        isEmailVerified: true,
        isLatitudeIdPasswordSet: true,
        applicationSubmitted: true,
        applicationStartDate: new Date().toISOString(),
        applicationErrors: null,
        acquisition: nzV2AcquisitionComplete,
      }),
    },
    {
      label: 'Interactive IDV',
      key: 'eidv',
      path: STEP_EIDV,
      getState: () => ({
        activeStep: STEP_EIDV,
        applicationStatus: IDV_CHECK_REQUIRED,
        isEmailVerified: true,
        isLatitudeIdPasswordSet: true,
        applicationSubmitted: true,
        applicationStartDate: new Date().toISOString(),
        applicationErrors: null,
        acquisition: nzV2AcquisitionComplete,
      }),
    },
    {
      label: 'ID document uploader',
      key: 'id-doc-upload',
      path: STEP_ID_DOC_UPLOAD,
      getState: () => ({
        activeStep: STEP_ID_DOC_UPLOAD,
        applicationStatus: IDV_CHECK_REQUIRED,
        isEmailVerified: true,
        isLatitudeIdPasswordSet: true,
        applicationSubmitted: true,
        applicationStartDate: new Date().toISOString(),
        applicationErrors: null,
        idvAttempt: {
          NZDriversLicence: true,
          NZPassport: true,
          AuPassport: true,
        },
      }),
    },
    {
      label: 'Facial Biometrics Consent',
      key: 'facial-biometrics-consent',
      path: STEP_FACIAL_BIOMETRICS_CONSENT,
      getState: () => ({
        activeStep: STEP_FACIAL_BIOMETRICS_CONSENT,
        applicationStatus: BIOMETRICS_CONSENT_PENDING,
        isEmailVerified: true,
        isLatitudeIdPasswordSet: true,
        applicationSubmitted: true,
        applicationStartDate: new Date().toISOString(),
        applicationErrors: null,
        acquisition: nzV2AcquisitionComplete,
      }),
    },
    {
      label: 'Processing Biometrics (SMS sent)',
      key: 'processing-biometrics',
      path: '/processing-biometrics',
      getState: () => ({
        activeStep: '/processing-biometrics',
        applicationStatus: BIOMETRICS_CHECK_REQUIRED,
        isEmailVerified: true,
        isLatitudeIdPasswordSet: true,
        applicationSubmitted: true,
        applicationStartDate: new Date().toISOString(),
        applicationErrors: null,
        acquisition: nzV2AcquisitionComplete,
        maskedMobileNumber: '044 *** 234',
        applicationRef: '7323655040880',
      }),
    },
    {
      label: 'Proof of Income - Methods',
      key: 'proof-of-income-methods',
      path: '/verify-income',
      getState: () => ({
        acquisition: nzV2AcquisitionComplete,
        applicationSubmitted: true,
        applicationStatus: CONDITIONALLY_APPROVED,
        applicationStatusReason: 'PROOF_OF_INCOME',
        applicationErrors: null,
        applicationStartDate: new Date().toISOString(),
        gemVisaAccountNumber: '5218 9435 0015 199',
      }),
    },
    {
      label: 'Proof of Income - Methods - Merchant Apple',
      key: 'proof-of-income-methods-merchant-apple',
      condition: () => isGemAU(),
      path: '/verify-income',
      getState: () => ({
        acquisition: nzV2AcquisitionComplete,
        applicationSubmitted: true,
        applicationStatus: CONDITIONALLY_APPROVED,
        applicationStatusReason: 'PROOF_OF_INCOME',
        applicationErrors: null,
        applicationStartDate: new Date().toISOString(),
        gemVisaAccountNumber: '5218 9435 0015 199',
        applyAndBuy: {
          channel: 'ONLINE',
          merchantId: '700021',
          merchantGroupName: 'Apple',
          merchantName: 'Apple Online',
          shortCode: '489',
          upstreamUrl: 'https://www.apple.com/au/shop/browse/financing',
          storeFinderUrl: 'https://www.latitudefinancial.com.au/interest-free/find-a-store/',
        },
      }),
    },
    {
      label: 'Proof of Income - Proviso',
      key: 'proof-of-income-proviso',
      path: '/verify-ficonnect',
      getState: () => ({
        acquisition: nzV2AcquisitionComplete,
        applicationSubmitted: true,
        applicationStatus: CONDITIONALLY_APPROVED,
        applicationStatusReason: 'PROOF_OF_INCOME',
        applicationErrors: null,
        applicationStartDate: new Date().toISOString(),
        gemVisaAccountNumber: '5218 9435 0015 199',
      }),
    },
    {
      label: 'Proof of Income - Document Upload',
      key: 'proof-of-income-document-upload',
      path: '/verify-document-upload',
      getState: () => ({
        acquisition: nzV2AcquisitionComplete,
        applicationSubmitted: true,
        applicationStatus: CONDITIONALLY_APPROVED,
        applicationStatusReason: 'PROOF_OF_INCOME',
        applicationErrors: null,
        applicationStartDate: new Date().toISOString(),
        gemVisaAccountNumber: '5218 9435 0015 199',
      }),
    },
    {
      label: 'Proof of Income - Document Upload - Timeout',
      key: 'proof-of-income-document-upload-timeout',
      path: '/outcome-timeout',
      getState: () => ({
        acquisition: nzV2AcquisitionComplete,
        applicationSubmitted: true,
        applicationStatus: POI_CHECK_PENDING,
        incomeVerificationType: POITYPE_DOC_UPLOAD,
        applicationErrors: null,
        applicationStartDate: new Date().toISOString(),
        gemVisaAccountNumber: '5218 9435 0015 199',
      }),
    },
    {
      label: 'Proof of Identity - Anti-Money Laundering - AML',
      key: 'proof-of-identity-anti-money-laundering',
      path: STEP_VERIFY_IDENTITY,
      getState: () => ({
        acquisition: nzV2AcquisitionComplete,
        applicationSubmitted: true,
        applicationStatus: CONDITIONALLY_APPROVED,
        applicationStatusReason: ANTI_MONEY_LAUNDERING,
        applicationErrors: null,
        applicationStartDate: new Date().toISOString(),
        creditLimitApproved: 3000,
        applicationRef: '7323655040880',
      }),
    },
    {
      label: 'Proof of Address',
      key: 'proof-of-address',
      path: '/proof-of-address',
      getState: () => ({
        acquisition: nzV2AcquisitionComplete,
        applicationSubmitted: true,
        applicationStatus: CONDITIONALLY_APPROVED,
        applicationStatusReason: PROOF_OF_ADDRESS,
        creditLimitApproved: 3000,
        applicationErrors: null,
        applicationStartDate: new Date().toISOString(),
        gemVisaAccountNumber: '5218 9435 0015 199',
        applicationRef: '7323655040880',
      }),
    },
    {
      label: 'Outcome - Approved',
      key: 'outcome-approved',
      path: STEP_OUTCOME,
      getState: () => ({
        acquisition: nzV2AcquisitionComplete,
        applicationSubmitted: true,
        applicationStatus: 'APPROVED',
        applicationErrors: null,
        applicationStartDate: new Date().toISOString(),
        applicationDate: new Date().toISOString(),
        finalDecisionDate: new Date().toISOString(),
        accountNumber: '5218 9435 0015 199',
        creditLimitApproved: 3000,
        applicationRef: '7323655040880',
      }),
    },
    {
      label: 'Outcome - Approved - Merchant Apple',
      key: 'outcome-approved-merchant-apple',
      condition: () => isGemAU(),
      path: STEP_OUTCOME,
      getState: () => ({
        acquisition: nzV2AcquisitionComplete,
        applicationSubmitted: true,
        applicationStatus: 'APPROVED',
        applicationErrors: null,
        applicationStartDate: new Date().toISOString(),
        applicationDate: new Date().toISOString(),
        finalDecisionDate: new Date().toISOString(),
        accountNumber: '5218 9435 0015 199',
        creditLimitApproved: 3000,
        applicationRef: '7323655040880',
        applyAndBuy: {
          channel: 'ONLINE',
          merchantId: '700021',
          merchantGroupName: 'Apple',
          merchantName: 'Apple Online',
          shortCode: '489',
          upstreamUrl: 'https://www.apple.com/au/shop/browse/financing',
          storeFinderUrl: 'https://www.latitudefinancial.com.au/interest-free/find-a-store/',
        },
      }),
    },
    {
      label: 'Outcome - Declined',
      key: 'outcome-declined',
      path: STEP_OUTCOME,
      getState: () => ({
        acquisition: nzV2AcquisitionComplete,
        applicationSubmitted: true,
        applicationStatus: DECLINED,
        applicationErrors: null,
        applicationStartDate: new Date().toISOString(),
      }),
    },
    {
      label: 'Outcome - Referred',
      key: 'outcome-referred',
      path: STEP_OUTCOME,
      getState: () => ({
        acquisition: nzV2AcquisitionComplete,
        applicationSubmitted: true,
        applicationStatus: REFERRED,
        applicationErrors: null,
        applicationStartDate: new Date().toISOString(),
      }),
    },
    {
      label: 'Outcome - Referred - Merchant Apple',
      key: 'outcome-referred-merchant-apple',
      condition: () => isGemAU(),
      path: STEP_OUTCOME,
      getState: () => ({
        acquisition: nzV2AcquisitionComplete,
        applicationSubmitted: true,
        applicationStatus: REFERRED,
        applicationErrors: null,
        applicationStartDate: new Date().toISOString(),
        applyAndBuy: {
          channel: 'ONLINE',
          merchantId: '700021',
          merchantGroupName: 'Apple',
          merchantName: 'Apple Online',
          shortCode: '489',
          upstreamUrl: 'https://www.apple.com/au/shop/browse/financing',
          storeFinderUrl: 'https://www.latitudefinancial.com.au/interest-free/find-a-store/',
        },
      }),
    },
    {
      label: 'Outcome - Timeout',
      key: 'outcome-timeout',
      path: '/outcome-timeout',
      getState: () => ({
        acquisition: nzV2AcquisitionComplete,
        applicationSubmitted: true,
        applicationStatus: PENDING,
        applicationErrors: null,
        applicationStartDate: new Date().toISOString(),
      }),
    },
    {
      label: 'Outcome - System Unavailable',
      key: 'outcome-system-unavailable',
      path: STEP_OUTCOME,
      getState: () => ({
        acquisition: {
          overview: {
            consentGiven: true,
            hasReadImportantDocuments: true,
          },
          contactDetails: {},
          password: {},
          personalDetails: {},
          otherDetails: {},
          extraDetails: {},
          employmentDetails: {},
          expensesDetails: {},
          additionalDebtsDetails: {},
        },
        applicationSubmitted: true,
        applicationStatus: SERVICE_UNAVAILABLE,
        applicationErrors: null,
        applicationStartDate: new Date().toISOString(),
      }),
    },
  ];

  return {
    AU: [
      {
        label: 'Saved for later',
        key: 'saved-for-later',
        path: '/saved',
        getState: () => ({
          acquisition: auAcquisitionComplete,
          applicationSubmitted: true,
          applicationStatus: POI_CHECK_REQUIRED,
          applicationErrors: null,
          applicationStartDate: new Date().toISOString(),
          gemVisaAccountNumber: '5218 9435 0015 199',
        }),
      },
      {
        label: 'Resume - Expired',
        key: 'resume-expired',
        path: '/resume-application-expired',
        getState: () => ({
          acquisition: auAcquisitionComplete,
          applicationStatus: IDV_CHECK_REQUIRED,
          isEmailVerified: true,
          isLatitudeIdPasswordSet: true,
          applicationSubmitted: true,
          applicationStartDate: new Date().toISOString(),
          applicationErrors: null,
          creditLimitApproved: 3000,
        }),
      },
      {
        label: 'Commitments',
        key: 'commitments',
        path: '/commitments',
        getState: () => ({
          acquisition: auAcquisitionComplete,
          applicationSubmitted: false,
          applicationStartDate: new Date().toISOString(),
        }),
      },
      {
        label: 'Additional Card Holder',
        key: 'additional-card-holder',
        path: '/additional-card-holder',
        getState: () => ({
          acquisition: auAcquisitionComplete,
          applicationSubmitted: false,
          applicationStartDate: new Date().toISOString(),
        }),
      },
      {
        label: 'Balance Transfer',
        key: 'balance-transfer',
        path: '/balance-transfer',
        getState: () => ({
          acquisition: {
            ...auAcquisitionComplete,
            additionalDebtsDetails: {
              hasOtherDebts: 'yes',
              secondaryPropertyMortgageRentBoard: {
                amount: '100',
                frequency: 'monthly',
              },
              totalCardLimits: '100',
              amountOwingOnCreditCard: '100',
              loanRepayment: {
                amount: '100',
                frequency: 'monthly',
              },
              hasLoanLumpSumPayment: 'no',
              payLumpSumWithExistingSaving: '',
            },
            creditLimitRequest: {
              creditLimitOptions: '',
              newCreditLimit: '',
            },
            achDetails: {
              hasAch: null,
              sameCurrentAddress: 'yes',
              personalDetails: {
                hasMiddleName: 'no',
                title: '',
                firstName: '',
                lastName: '',
                middleName: '',
                dateOfBirth: {
                  day: '',
                  month: '',
                  year: '',
                },
                mobileNumber: '',
                emailAddress: '',
              },
              currentAddress: {
                useManualAddress: false,
                address: {
                  unitNumber: '',
                  streetNumber: '',
                  streetName: '',
                  streetType: '',
                  suburb: '',
                  postcode: '',
                  poBoxNumber: '',
                  addressType: '',
                  countryCode: '',
                  state: '',
                  city: '',
                },
              },
            },
          },
          applicationSubmitted: false,
          applicationStartDate: new Date().toISOString(),
        }),
      },
      {
        label: 'Processing Submission',
        key: 'processing-submission',
        path: '/processing',
        getState: () => ({
          acquisition: auAcquisitionComplete,
          applicationSubmitted: true,
          applicationStatus: PENDING,
          applicationErrors: null,
          applicationStartDate: new Date().toISOString(),
        }),
      },
      {
        label: 'CCR Loan Information',
        key: 'ccr-loan-information',
        path: STEP_LOAN_INFORMATION,
        getState: () => ({
          acquisition: {
            ...auAcquisitionComplete,
            otherDetails: {
              ...auAcquisitionComplete.otherDetails,
              residentialStatus: 'MORTGAGE',
            },
          },
          applicationStatus: CCR_REQUIRED,
          applicationSubmitted: true,
          applicationStartDate: new Date().toISOString(),
          applicationErrors: null,
          undisclosedDebts: [
            {
              id: 1,
              source: 'EQUIFAX_AUSTRALIA',
              category: 'HOME_LOAN',
              creditProvider: 'ANZ',
              currentLimit: 10000.0,
            },
            {
              id: 2,
              source: 'EQUIFAX_AUSTRALIA',
              category: 'HOME_LOAN',
              creditProvider: 'ANZ',
              currentLimit: 20000.0,
            },
            {
              id: 3,
              source: 'EQUIFAX_AUSTRALIA',
              category: 'CREDIT_CARD',
              creditProvider: 'Latitude',
              currentLimit: 3000.0,
            },
          ],
        }),
      },
      {
        label: 'Dynamic Credit Limit - Conditionally Approved',
        key: 'dynamic-credit-limit-conditionally-approved',
        path: STEP_CONFIRM_CREDIT_LIMIT,
        getState: () => ({
          acquisition: {
            ...auAcquisitionComplete,
            confirmCreditLimit: { newCreditLimit: '', creditLimitOptions: null },
          },
          applicationStatus: QUOTE_INITIAL,
          applicationSubmitted: true,
          applicationStartDate: new Date().toISOString(),
          applicationErrors: null,
          creditLimitApproved: 7000,
        }),
      },
      {
        label: 'Dynamic Credit Limit - Declined',
        key: 'dynamic-credit-limit-declined',
        path: STEP_CONFIRM_CREDIT_LIMIT,
        getState: () => ({
          acquisition: {
            ...{
              overview: {
                consentGiven: true,
                hasReadImportantDocuments: true,
              },
              contactDetails: {},
              password: {},
              personalDetails: {},
              otherDetails: {},
              extraDetails: {},
              employmentDetails: {},
              expensesDetails: {},
              additionalDebtsDetails: {},
              confirmCreditLimit: { referred: true },
              preferredCreditLimitDetails: { referred: true },
            },
            confirmReferredCreditLimit: {
              newCreditLimit: '',
            },
          },
          applicationStatus: QUOTE_DECLINE,
          applicationSubmitted: true,
          applicationStartDate: new Date().toISOString(),
          applicationErrors: null,
        }),
      },
      {
        label: 'Dynamic Credit Limit - Conditional Approved - Min Limit DJ Premiere',
        key: 'dynamic-credit-limit-conditional-approved-min-limit-dj-premiere',
        path: STEP_CONFIRM_CREDIT_LIMIT,
        getState: () => ({
          acquisition: {
            ...auAcquisitionComplete,
            confirmCreditLimit: { newCreditLimit: '', creditLimitOptions: null },
          },
          applicationStatus: QUOTE_INITIAL,
          applicationSubmitted: true,
          applicationStartDate: new Date().toISOString(),
          applicationErrors: null,
          creditLimitApproved: 1000,
        }),
      },
      {
        label: 'Dynamic Credit Limit - Conditional Approved - Min Limit DJ Prestige',
        key: 'dynamic-credit-limit-conditional-approved-min-limit-dj-prestige',
        path: STEP_CONFIRM_CREDIT_LIMIT,
        getState: () => ({
          acquisition: {
            ...auAcquisitionComplete,
            confirmCreditLimit: { newCreditLimit: '', creditLimitOptions: null },
          },
          applicationStatus: QUOTE_INITIAL,
          applicationSubmitted: true,
          applicationStartDate: new Date().toISOString(),
          applicationErrors: null,
          creditLimitApproved: 6000,
        }),
      },
      {
        label: 'Dynamic Credit Allocation - BT',
        key: 'dynamic-credit-allocation-bt',
        path: STEP_CREDIT_LIMIT_ALLOCATION,
        getState: () => ({
          acquisition: {
            ...auAcquisitionComplete,
            confirmCreditLimit: { newCreditLimit: '', creditLimitOptions: null },
            additionalDebtsDetails: {
              hasOtherDebts: 'yes',
              secondaryPropertyMortgageRentBoard: {
                amount: '100',
                frequency: 'monthly',
              },
              totalCardLimits: '100',
              amountOwingOnCreditCard: '100',
              loanRepayment: {
                amount: '100',
                frequency: 'monthly',
              },
              hasLoanLumpSumPayment: 'no',
              payLumpSumWithExistingSaving: '',
            },
            achDetails: {
              hasAch: null,
            },
          },
          applicationStatus: QUOTE_INITIAL,
          applicationSubmitted: true,
          applicationStartDate: new Date().toISOString(),
          applicationErrors: null,
          creditLimitApproved: 6000,
        }),
      },
      {
        label: 'Latitude ID - Introduction',
        key: 'latitude-id-introduction',
        path: '/latitude-id-introduction',
        getState: () => ({
          acquisition: auAcquisitionComplete,
          applicationSubmitted: true,
          applicationStatus: IDV_CHECK_REQUIRED,
          applicationErrors: null,
          applicationStartDate: new Date().toISOString(),
          isEmailVerified: false,
          isLatitudeIdPasswordSet: false,
        }),
      },
      {
        label: 'Latitude ID - Password',
        key: 'latitude-id-password',
        path: '/latitude-id',
        getState: () => ({
          acquisition: auAcquisitionComplete,
          applicationSubmitted: true,
          applicationStatus: IDV_CHECK_REQUIRED,
          applicationErrors: null,
          applicationStartDate: new Date().toISOString(),
          isEmailVerified: true,
          isLatitudeIdPasswordSet: false,
        }),
      },
      {
        label: 'Proof of Identity - EIDV - DVS Consent Required',
        key: 'proof-of-identity-eidv-dvs-consent-required',
        path: STEP_DVS_CONSENT,
        getState: () => ({
          acquisition: auAcquisitionComplete,
          applicationSubmitted: true,
          applicationStatus: IDV_CHECK_REQUIRED,
          isEmailVerified: true,
          isLatitudeIdPasswordSet: true,
          applicationErrors: null,
          applicationStartDate: new Date().toISOString(),
        }),
      },
      {
        label: 'Proof of Identity - EIDV - Methods',
        key: 'proof-of-identity-eidv-methods',
        path: STEP_EIDV,
        getState: () => ({
          acquisition: auAcquisitionComplete,
          applicationStatus: IDV_CHECK_REQUIRED,
          isEmailVerified: true,
          isLatitudeIdPasswordSet: true,
          applicationSubmitted: true,
          applicationStartDate: new Date().toISOString(),
          applicationErrors: null,
        }),
      },
      {
        label: 'Proof of Identity - EIDV - Pending',
        key: 'proof-of-identity-eidv-pending',
        path: STEP_PROCESSING_IDV,
        getState: () => ({
          acquisition: auAcquisitionComplete,
          applicationSubmitted: true,
          applicationStatus: IDV_CHECK_PENDING,
          applicationErrors: null,
          applicationStartDate: new Date().toISOString(),
        }),
      },
      {
        label: 'Proof of Identity - Processing Biometrics',
        key: 'proof-of-identity-processing-biometrics',
        path: '/processing-biometrics',
        getState: () => ({
          acquisition: auAcquisitionComplete,
          applicationSubmitted: true,
          applicationStatus: BIOMETRICS_CHECK_REQUIRED,
          applicationErrors: null,
          isEmailVerified: true,
          isLatitudeIdPasswordSet: true,
          applicationStartDate: new Date().toISOString(),
          gemVisaAccountNumber: '5218 9435 0015 199',
          maskedMobileNumber: '044 *** 234',
        }),
      },
      {
        label: 'Proof of Identity - Australia Post',
        key: 'proof-of-identity-australia-post',
        path: STEP_AUS_POST,
        getState: () => ({
          acquisition: auAcquisitionComplete,
          applicationStatus: IDV_CHECK_REQUIRED,
          isEmailVerified: true,
          isLatitudeIdPasswordSet: true,
          applicationSubmitted: true,
          applicationStartDate: new Date().toISOString(),
          applicationErrors: null,
        }),
      },
      {
        label: 'Proof of Identity - In Store',
        key: 'proof-of-identity-in-store',
        path: STEP_VERIFY_INSTORE,
        getState: () => ({
          acquisition: auAcquisitionComplete,
          applicationStatus: IDV_CHECK_REQUIRED,
          isEmailVerified: true,
          isLatitudeIdPasswordSet: true,
          applicationSubmitted: true,
          applicationStartDate: new Date().toISOString(),
          applicationRef: '7014625177363',
          applicationErrors: null,
        }),
      },
      {
        label: 'Proof of Identity - In Store - Merchant Apple',
        key: 'proof-of-identity-in-store-merchant-apple',
        condition: () => isGemAU(),
        path: STEP_VERIFY_INSTORE,
        getState: () => ({
          acquisition: auAcquisitionComplete,
          applicationStatus: IDV_CHECK_REQUIRED,
          isEmailVerified: true,
          isLatitudeIdPasswordSet: true,
          applicationSubmitted: true,
          applicationStartDate: new Date().toISOString(),
          applicationRef: '7014625177363',
          applicationErrors: null,
          applyAndBuy: {
            channel: 'ONLINE',
            merchantId: '700021',
            merchantGroupName: 'Apple',
            merchantName: 'Apple Online',
            shortCode: '489',
            upstreamUrl: 'https://www.apple.com/au/shop/browse/financing',
            storeFinderUrl: 'https://www.latitudefinancial.com.au/interest-free/find-a-store/',
          },
        }),
      },
      {
        label: 'Proof of Income - Methods',
        key: 'proof-of-income-methods',
        path: '/verify-income',
        getState: () => ({
          acquisition: auAcquisitionComplete,
          applicationSubmitted: true,
          applicationStatus: POI_CHECK_REQUIRED,
          applicationErrors: null,
          applicationStartDate: new Date().toISOString(),
          gemVisaAccountNumber: '5218 9435 0015 199',
        }),
      },
      {
        label: 'Proof of Income - Methods - Merchant Apple',
        key: 'proof-of-income-methods-merchant-apple',
        condition: () => isGemAU(),
        path: '/verify-income',
        getState: () => ({
          acquisition: auAcquisitionComplete,
          applicationSubmitted: true,
          applicationStatus: POI_CHECK_REQUIRED,
          applicationErrors: null,
          applicationStartDate: new Date().toISOString(),
          gemVisaAccountNumber: '5218 9435 0015 199',
          applyAndBuy: {
            channel: 'ONLINE',
            merchantId: '700021',
            merchantGroupName: 'Apple',
            merchantName: 'Apple Online',
            shortCode: '489',
            upstreamUrl: 'https://www.apple.com/au/shop/browse/financing',
            storeFinderUrl: 'https://www.latitudefinancial.com.au/interest-free/find-a-store/',
          },
        }),
      },
      {
        label: 'Proof of Income - Proviso',
        key: 'proof-of-income-proviso',
        path: '/verify-ficonnect',
        getState: () => ({
          acquisition: auAcquisitionComplete,
          applicationSubmitted: true,
          applicationStatus: POI_CHECK_REQUIRED,
          applicationErrors: null,
          applicationStartDate: new Date().toISOString(),
          gemVisaAccountNumber: '5218 9435 0015 199',
        }),
      },
      {
        label: 'Proof of Income - Document Upload',
        key: 'proof-of-income-document-upload',
        path: '/verify-document-upload',
        getState: () => ({
          acquisition: auAcquisitionComplete,
          applicationSubmitted: true,
          applicationStatus: POI_CHECK_REQUIRED,
          applicationErrors: null,
          applicationStartDate: new Date().toISOString(),
          gemVisaAccountNumber: '5218 9435 0015 199',
        }),
      },
      {
        label: 'Proof of Income - Document Upload - Timeout',
        key: 'proof-of-income-document-upload-timeout',
        path: '/outcome-timeout',
        getState: () => ({
          acquisition: auAcquisitionComplete,
          applicationSubmitted: true,
          applicationStatus: POI_CHECK_PENDING,
          incomeVerificationType: POITYPE_DOC_UPLOAD,
          applicationErrors: null,
          applicationStartDate: new Date().toISOString(),
          gemVisaAccountNumber: '5218 9435 0015 199',
        }),
      },
      {
        label: 'Outcome - Approved',
        key: 'outcome-approved',
        path: STEP_OUTCOME,
        getState: () => ({
          acquisition: auAcquisitionComplete,
          applicationSubmitted: true,
          applicationStatus: 'APPROVED',
          applicationErrors: null,
          applicationStartDate: new Date().toISOString(),
          applicationDate: new Date().toISOString(),
          finalDecisionDate: new Date().toISOString(),
          accountNumber: '5218 9435 0015 199',
          creditLimitApproved: 3000,
          applicationRef: '7323655040880',
        }),
      },
      {
        label: 'Outcome - Approved - Merchant Apple',
        key: 'outcome-approved-merchant-apple',
        condition: () => isGemAU(),
        path: STEP_OUTCOME,
        getState: () => ({
          acquisition: auAcquisitionComplete,
          applicationSubmitted: true,
          applicationStatus: 'APPROVED',
          applicationErrors: null,
          applicationStartDate: new Date().toISOString(),
          applicationDate: new Date().toISOString(),
          finalDecisionDate: new Date().toISOString(),
          accountNumber: '5218 9435 0015 199',
          creditLimitApproved: 3000,
          applicationRef: '7323655040880',
          applyAndBuy: {
            channel: 'ONLINE',
            merchantId: '700021',
            merchantGroupName: 'Apple',
            merchantName: 'Apple Online',
            shortCode: '489',
            upstreamUrl: 'https://www.apple.com/au/shop/browse/financing',
            storeFinderUrl: 'https://www.latitudefinancial.com.au/interest-free/find-a-store/',
          },
        }),
      },
      {
        label: 'Outcome - Approved - Merchant Harvey Norman',
        key: 'outcome-approved-merchant-harvey-norman',
        condition: () => config.productCode === PRODUCT_CODE_GO,
        path: STEP_OUTCOME,
        getState: () => ({
          acquisition: auAcquisitionComplete,
          applicationSubmitted: true,
          applicationStatus: 'APPROVED',
          applicationErrors: null,
          applicationStartDate: new Date().toISOString(),
          applicationDate: new Date().toISOString(),
          finalDecisionDate: new Date().toISOString(),
          accountNumber: '5218 9435 0015 199',
          creditLimitApproved: 3000,
          applicationRef: '7323655040880',
          applyAndBuy: {
            channel: 'ONLINE',
            merchantId: '000406911',
            merchantGroupName: 'Harvey Norman',
            merchantName: 'Harvey Norman Online',
            shortCode: '408',
            upstreamUrl: 'https://www.harveynorman.com.au/',
            storeFinderUrl: 'https://stores.harveynorman.com.au/',
          },
        }),
      },
      {
        label: 'Outcome - Declined',
        key: 'outcome-declined',
        path: STEP_OUTCOME,
        getState: () => ({
          acquisition: auAcquisitionComplete,
          applicationSubmitted: true,
          applicationStatus: DECLINED,
          applicationErrors: null,
          applicationStartDate: new Date().toISOString(),
        }),
      },
      {
        label: 'Outcome - Referred',
        key: 'outcome-referred',
        path: STEP_OUTCOME,
        getState: () => ({
          acquisition: auAcquisitionComplete,
          applicationSubmitted: true,
          applicationStatus: REFERRED,
          applicationErrors: null,
          applicationStartDate: new Date().toISOString(),
        }),
      },
      {
        label: 'Outcome - Referred - Merchant Apple',
        key: 'outcome-referred-merchant-apple',
        condition: () => isGemAU(),
        path: STEP_OUTCOME,
        getState: () => ({
          acquisition: auAcquisitionComplete,
          applicationSubmitted: true,
          applicationStatus: REFERRED,
          applicationErrors: null,
          applicationStartDate: new Date().toISOString(),
          applyAndBuy: {
            channel: 'ONLINE',
            merchantId: '700021',
            merchantGroupName: 'Apple',
            merchantName: 'Apple Online',
            shortCode: '489',
            upstreamUrl: 'https://www.apple.com/au/shop/browse/financing',
            storeFinderUrl: 'https://www.latitudefinancial.com.au/interest-free/find-a-store/',
          },
        }),
      },
      {
        label: 'Outcome - Timeout',
        key: 'outcome-timeout',
        path: '/outcome-timeout',
        getState: () => ({
          acquisition: auAcquisitionComplete,
          applicationSubmitted: true,
          applicationStatus: PENDING,
          applicationErrors: null,
          applicationStartDate: new Date().toISOString(),
        }),
      },
      {
        label: 'Outcome - System Unavailable',
        key: 'outcome-system-unavailable',
        path: STEP_OUTCOME,
        getState: () => ({
          acquisition: {
            overview: {
              consentGiven: true,
              hasReadImportantDocuments: true,
            },
            contactDetails: {},
            password: {},
            personalDetails: {},
            otherDetails: {},
            extraDetails: {},
            employmentDetails: {},
            expensesDetails: {},
            additionalDebtsDetails: {},
          },
          applicationSubmitted: true,
          applicationStatus: SERVICE_UNAVAILABLE,
          applicationErrors: null,
          applicationStartDate: new Date().toISOString(),
        }),
      },
      {
        label: 'Outcome - Conditionally Approved - POA',
        key: 'outcome-conditionally-approved-poa',
        path: STEP_OUTCOME,
        getState: () => ({
          acquisition: auAcquisitionComplete,
          applicationSubmitted: true,
          applicationStatus: CONDITIONALLY_APPROVED,
          applicationStatusReason: PROOF_OF_ADDRESS,
          applicationErrors: null,
          applicationStartDate: new Date().toISOString(),
          creditLimitApproved: 3000,
        }),
      },
      {
        label: 'Outcome - Conditionally Approved - AML',
        key: 'outcome-conditionally-approved-aml',
        path: STEP_OUTCOME,
        getState: () => ({
          acquisition: auAcquisitionComplete,
          applicationSubmitted: true,
          applicationStatus: CONDITIONALLY_APPROVED,
          applicationStatusReason: ANTI_MONEY_LAUNDERING,
          applicationErrors: null,
          applicationStartDate: new Date().toISOString(),
          creditLimitApproved: 3000,
        }),
      },
      {
        label: 'Outcome - Conditionally Approved - POI',
        key: 'outcome-conditionally-approved-poi',
        path: STEP_OUTCOME,
        getState: () => ({
          acquisition: auAcquisitionComplete,
          applicationSubmitted: true,
          applicationStatus: CONDITIONALLY_APPROVED,
          applicationStatusReason: PROOF_OF_INCOME,
          applicationErrors: null,
          applicationStartDate: new Date().toISOString(),
          creditLimitApproved: 3000,
        }),
      },
      {
        label: 'Outcome - Conditionally Approved - POA & POI',
        key: 'outcome-conditionally-approved-poa-poi',
        path: STEP_OUTCOME,
        getState: () => ({
          acquisition: auAcquisitionComplete,
          applicationSubmitted: true,
          applicationStatus: CONDITIONALLY_APPROVED,
          applicationStatusReason: PROOF_OF_ADDRESS_AND_PROOF_OF_INCOME,
          applicationErrors: null,
          applicationStartDate: new Date().toISOString(),
          creditLimitApproved: 3000,
        }),
      },
      {
        label: 'Outcome - Conditionally Approved - AML & POI',
        key: 'outcome-conditionally-approved-aml-poi',
        path: STEP_OUTCOME,
        getState: () => ({
          acquisition: auAcquisitionComplete,
          applicationSubmitted: true,
          applicationStatus: CONDITIONALLY_APPROVED,
          applicationStatusReason: ANTI_MONEY_LAUNDERING_AND_PROOF_OF_INCOME,
          applicationErrors: null,
          applicationStartDate: new Date().toISOString(),
          creditLimitApproved: 3000,
        }),
      },
      {
        label: 'Financial Table',
        key: 'financial-table',
        path: `/outcome-fin-table?creditLimit=3,000&accountNumber=5218%209435%200015%20199&applicationDate=2024-07-31T05:54:58.945Z&productCode=${config.productCode}`,
        getState: () => ({
          acquisition: auAcquisitionComplete,
          applicationSubmitted: true,
          applicationStatus: APPROVED,
          applicationErrors: null,
          applicationDate: new Date().toISOString(),
          accountNumber: '5218 9435 0015 199',
          creditLimitApproved: 3000,
        }),
      },
    ],
    NZ: nzV2,
  };
};

const DUMMY_APPLICATION_ID = 'f1ef8821-2bc9-454f-a463-c8f0fbea9274';

function buildState(state = {}) {
  const defaultState = getDefaultState();
  return defaultsDeep({ applicationId: DUMMY_APPLICATION_ID, ...state }, defaultState);
}
